import { ExternalLink, GitCommit, KeyRound, ShieldCheck } from "lucide-react";
import { getCurrentPrincipal } from "@/api/client";
import { useHealth } from "@/api/queries";
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const SIGNARIS_AUTH_URL =
  import.meta.env.VITE_SIGNARIS_AUTH_URL || "https://auth.signaris.ru";

const FRONTEND_BUILD_SHA = __BUILD_SHA__;
const FRONTEND_BUILD_AT = __BUILD_AT__;

/**
 * Phase 3.1: пароли + MFA + invitation/welcome — всё в signaris-auth.
 * NET-страница «Настройки» теперь показывает только профиль из JWT и
 * ссылается на signaris-auth для управления учёткой.
 */
export function SettingsPage() {
  const principal = getCurrentPrincipal();
  if (!principal) return null;

  return (
    <div className="space-y-6">
      <header>
        <span className="label">Профиль</span>
        <h1 className="font-display text-3xl font-bold mt-2">Настройки</h1>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Учётная запись</CardTitle>
          <CardDescription>
            Аккаунт управляется в{" "}
            <a
              href={SIGNARIS_AUTH_URL}
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline"
            >
              signaris-auth
            </a>{" "}
            — едином IdP всех продуктов Signaris.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          <KeyValue label="Email" value={principal.email} />
          <KeyValue label="Имя" value={principal.full_name} />
          <KeyValue
            label="Роль (NET)"
            value={
              <Badge tone={principal.is_superadmin ? "accent" : "info"}>
                {principal.net_role ?? "—"}
              </Badge>
            }
          />
          <KeyValue
            label="Организация"
            value={<span className="font-mono">{principal.tenant_slug}</span>}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound size={16} /> Пароль
          </CardTitle>
          <CardDescription>
            Сменить пароль можно в личном кабинете signaris-auth.
          </CardDescription>
        </CardHeader>
        <CardBody>
          <a
            href={`${SIGNARIS_AUTH_URL}/account/password`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-border text-sm hover:bg-white/5"
          >
            Сменить пароль <ExternalLink size={14} />
          </a>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck size={16} /> Двухфакторная аутентификация
          </CardTitle>
          <CardDescription>
            MFA настраивается в signaris-auth. Влияет на все продукты Signaris.
          </CardDescription>
        </CardHeader>
        <CardBody>
          <a
            href={`${SIGNARIS_AUTH_URL}/account/mfa`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-border text-sm hover:bg-white/5"
          >
            Настройки MFA <ExternalLink size={14} />
          </a>
        </CardBody>
      </Card>

      <BuildInfoCard />
    </div>
  );
}

function BuildInfoCard() {
  const { data: health, isLoading, isError } = useHealth();
  const backendSha = health?.build_sha ?? null;
  const backendAt = health?.build_at ?? null;
  // Сравниваем хеши только если оба известны и не "unknown".
  const mismatch =
    !!backendSha &&
    backendSha !== "unknown" &&
    FRONTEND_BUILD_SHA !== "unknown" &&
    backendSha !== FRONTEND_BUILD_SHA;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitCommit size={16} /> Версия сборки
        </CardTitle>
        <CardDescription>
          Git SHA и время сборки. Помогает сверить, что фронт и бэк
          задеплоены из одного коммита, и различить prod/staging.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-3">
        <KeyValue
          label="Frontend"
          value={
            <span className="font-mono text-xs">
              {FRONTEND_BUILD_SHA} · {formatBuildAt(FRONTEND_BUILD_AT)}
            </span>
          }
        />
        <KeyValue
          label="Backend"
          value={
            isLoading ? (
              <span className="text-fg-muted text-xs">загрузка…</span>
            ) : isError || !backendSha ? (
              <span className="text-fg-muted text-xs">недоступно</span>
            ) : (
              <span className="font-mono text-xs">
                {backendSha} · {formatBuildAt(backendAt)}
              </span>
            )
          }
        />
        {mismatch && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            Frontend и backend задеплоены из разных коммитов
            ({FRONTEND_BUILD_SHA} vs {backendSha}). Возможно идёт катящийся
            деплой — обнови страницу через минуту.
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function formatBuildAt(value: string | null): string {
  if (!value || value === "unknown") return "unknown";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

function KeyValue({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-sm text-fg-muted">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
